"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

const pricingPlans = [
  {
    name: "Freelancer",
    description: "Ideal para profesionales independientes",
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      "Hasta 3 cuentas bancarias",
      "Gestión de ingresos y gastos",
      "Soporte para 2 monedas",
      "Reportes básicos mensuales",
      "Recordatorios de pagos",
      "Exportación en PDF y Excel",
    ],
    highlighted: false,
    cta: "Comenzar gratis",
  },
  {
    name: "Impulso",
    description: "Perfecto para pequeñas empresas",
    monthlyPrice: 10,
    annualPrice: 100,
    features: [
      "Hasta 8 cuentas bancarias",
      "Categorización avanzada",
      "Soporte para 5 monedas",
      "Reportes detallados",
      "Conciliación automática",
      // "Facturación electrónica",
      // "Panel de proyecciones",
      "Soporte prioritario",
    ],
    highlighted: true,
    cta: "Comenzar gratis",
  },
  {
    name: "Visión",
    description: "Para negocios en crecimiento",
    monthlyPrice: 15,
    annualPrice: 150,
    features: [
      "Cuentas bancarias ilimitadas",
      "Múltiples usuarios (hasta 5)",
      "Todas las monedas disponibles",
      "Reportes personalizados",
      // "API para integraciones",
      "Conciliación avanzada",
      // "Gestión de roles y permisos",
      "Soporte 24/7",
      // "Onboarding personalizado",
    ],
    highlighted: false,
    cta: "Comenzar gratis",
  },
];

export default function Pricing() {
  const [mounted, setMounted] = useState(false);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <section id="precios" className="py-20 relative">
      {/* Background gradient effects */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-chart-2/10 rounded-full blur-3xl dark:bg-chart-2/5" />

      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Planes simples y
            <span className="bg-gradient-to-r from-chart-1 to-chart-2 text-transparent bg-clip-text block mt-1">
              transparentes
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Elige el plan que mejor se adapte a tus necesidades. Todos los
            planes incluyen acceso a las funciones esenciales.
          </p>

          <div className="flex items-center justify-center gap-4">
            <span className={annual ? "text-muted-foreground" : "font-medium"}>
              Mensual
            </span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <div className="flex items-center gap-1.5">
              <span
                className={annual ? "font-medium" : "text-muted-foreground"}
              >
                Anual
              </span>
              <Badge
                variant="outline"
                className="bg-chart-1/10 text-chart-1 border-chart-1/20"
              >
                2 meses gratis
              </Badge>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={cn("flex", plan.highlighted && "md:-mt-4 lg:-mt-8")}
            >
              <Card
                className={cn(
                  "flex flex-col w-full border-muted/40 bg-card/50 backdrop-blur transition-all duration-300 hover:shadow-md hover:border-muted/80",
                  plan.highlighted && "border-chart-1/30 shadow-lg"
                )}
              >
                {plan.highlighted && (
                  <div className="bg-gradient-to-r from-chart-1 to-chart-2 text-white text-center py-1.5 text-sm font-medium rounded-t-lg">
                    Más popular
                  </div>
                )}
                <CardHeader className="pb-8">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="mt-1.5">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold">
                        S/.
                        {annual
                          ? (plan.annualPrice / 12).toFixed(2)
                          : plan.monthlyPrice.toFixed(2)}
                      </span>
                      <span className="text-muted-foreground ml-2">/mes</span>
                    </div>
                    {annual && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Facturado como S/.{plan.annualPrice} por año
                      </p>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-chart-1 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="pt-6">
                  <Button
                    className={cn(
                      "w-full",
                      plan.highlighted
                        ? "bg-gradient-to-r from-chart-1 to-chart-2 hover:from-chart-1/90 hover:to-chart-2/90 text-white"
                        : "bg-muted"
                    )}
                    onClick={() => (window.location.href = "/register")}
                  >
                    {plan.cta}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
